import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';

/**
 * Minimal hash router: `#/orders/KD-20481?x=1` → { path: '/orders/KD-20481', params, query }.
 * Hash routing keeps the static builds deployable behind any web server with no rewrites,
 * and the browser back button keeps working.
 */
export type Route = { path: string; params: Record<string, string>; query: Record<string, string> };

type RouterValue = {
  route: Route;
  navigate: (to: string, options?: { replace?: boolean }) => void;
  back: (fallback?: string) => void;
  match: (pattern: string) => Record<string, string> | null;
};

const RouterContext = createContext<RouterValue | null>(null);

const parse = (hash: string): Route => {
  const raw = hash.replace(/^#/, '') || '/';
  const [path = '/', search = ''] = raw.split('?');
  return { path, params: {}, query: Object.fromEntries(new URLSearchParams(search)) };
};

export const matchPath = (pattern: string, path: string): Record<string, string> | null => {
  const p = pattern.split('/').filter(Boolean);
  const s = path.split('/').filter(Boolean);
  if (p.length !== s.length) return null;
  const params: Record<string, string> = {};
  for (let i = 0; i < p.length; i += 1) {
    const part = p[i]!;
    if (part.startsWith(':')) params[part.slice(1)] = decodeURIComponent(s[i]!);
    else if (part !== s[i]) return null;
  }
  return params;
};

export const RouterProvider = ({ children }: { children: ReactNode }) => {
  const [route, setRoute] = useState<Route>(() => parse(typeof window === 'undefined' ? '' : window.location.hash));

  useEffect(() => {
    const onChange = () => {
      setRoute(parse(window.location.hash));
      window.scrollTo(0, 0);
    };
    window.addEventListener('hashchange', onChange);
    return () => window.removeEventListener('hashchange', onChange);
  }, []);

  const navigate = useCallback((to: string, options?: { replace?: boolean }) => {
    const target = `#${to}`;
    if (options?.replace) window.location.replace(target);
    else window.location.hash = to;
  }, []);

  const back = useCallback((fallback = '/') => {
    if (window.history.length > 1) window.history.back();
    else window.location.hash = fallback;
  }, []);

  const match = useCallback((pattern: string) => matchPath(pattern, route.path), [route.path]);

  const value = useMemo(() => ({ route, navigate, back, match }), [route, navigate, back, match]);
  return <RouterContext.Provider value={value}>{children}</RouterContext.Provider>;
};

export const useRouter = (): RouterValue => {
  const value = useContext(RouterContext);
  if (!value) throw new Error('useRouter must be used inside <RouterProvider>');
  return value;
};

export type RouteTable = { pattern: string; render: (params: Record<string, string>) => ReactNode }[];

/** Renders the first matching route, or `fallback`. */
export const Routes = ({ table, fallback }: { table: RouteTable; fallback: ReactNode }) => {
  const { route } = useRouter();
  for (const entry of table) {
    const params = matchPath(entry.pattern, route.path);
    if (params) return <>{entry.render(params)}</>;
  }
  return <>{fallback}</>;
};
